import React from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

export type AlertVariant = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  message: string;
  onClose?: () => void;
  className?: string;
}

const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  message,
  onClose,
  className = '',
}) => {
  const variantStyles = {
    success: {
      container: 'bg-status-success/10 border-status-success/30',
      icon: <CheckCircle className="text-status-success" size={20} />,
      title: 'text-status-success',
      text: 'text-status-success/90',
    },
    error: {
      container: 'bg-status-danger/10 border-status-danger/30',
      icon: <AlertCircle className="text-status-danger" size={20} />,
      title: 'text-status-danger',
      text: 'text-status-danger/90',
    },
    warning: {
      container: 'bg-status-warning/10 border-status-warning/30',
      icon: <AlertTriangle className="text-status-warning" size={20} />,
      title: 'text-status-warning',
      text: 'text-status-warning/90',
    },
    info: {
      container: 'bg-accent-primary/10 border-accent-primary/30',
      icon: <Info className="text-accent-primary" size={20} />,
      title: 'text-accent-primary',
      text: 'text-accent-primary/90',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div
      className={`rounded-button border p-4 ${styles.container} ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{styles.icon}</div>
        <div className="flex-1">
          {title && (
            <h4 className={`font-semibold mb-1 ${styles.title}`}>{title}</h4>
          )}
          <p className={`text-sm ${styles.text}`}>{message}</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={`flex-shrink-0 ${styles.text} hover:opacity-70 transition-opacity`}
          >
            <X size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;
